import React from 'react'
import { IconButton, Box, keyframes, Tooltip } from '@mui/material'
import MicIcon from '@mui/icons-material/Mic'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { legacyTheme as theme } from '@/apps/legacy/shared/styles/theme'

type Props = {
  isRecording: boolean
  onClick: () => void
  disabled?: boolean
  size?: 'small' | 'medium' | 'large'
  onStopRecording?: () => void
  onCancelRecording?: () => void
}

// 録音中のアニメーション
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(220, 38, 38, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0);
  }
`

// 録音中の点滅アニメーション
const blink = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
`

// 録音中の波形アニメーション
const wave = keyframes`
  0%, 100% {
    transform: scaleY(1);
  }
  50% {
    transform: scaleY(1.5);
  }
`

const SimpleRecordButton: React.FC<Props> = ({
  isRecording,
  onClick,
  disabled = false,
  size = 'medium',
  onStopRecording,
  onCancelRecording,
}) => {
  const sizeMap = {
    small: { button: 40, icon: 20 },
    medium: { button: 48, icon: 24 },
    large: { button: 56, icon: 32 },
  }

  const { button: buttonSize, icon: iconSize } = sizeMap[size]

  if (isRecording && onStopRecording && onCancelRecording) {
    // 録音中は完了・中断ボタンを表示
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="録音を完了 (Shift+Enter)" placement="top">
          <IconButton
            onClick={onStopRecording}
            sx={{
              width: buttonSize,
              height: buttonSize,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
              boxShadow: 2,
              animation: `${pulse} 2s infinite`,
            }}
          >
            <CheckIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="録音を中断" placement="top">
          <IconButton
            onClick={onCancelRecording}
            sx={{
              width: buttonSize,
              height: buttonSize,
              bgcolor: theme.palette.grey[500],
              color: 'white',
              '&:hover': {
                bgcolor: theme.palette.grey[700],
              },
              boxShadow: 2,
            }}
          >
            <CloseIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: theme.palette.error.main,
          }}
        >
          <Box
            sx={{
              animation: `${blink} 1s ease-in-out infinite`,
            }}
          >
            ●
          </Box>
          <Box sx={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
            {[0, 1, 2].map((index) => (
              <Box
                key={index}
                sx={{
                  width: 3,
                  height: 12,
                  bgcolor: theme.palette.error.main,
                  borderRadius: 1,
                  animation: `${wave} 1s ease-in-out infinite`,
                  animationDelay: `${index * 0.15}s`,
                }}
              />
            ))}
          </Box>
          録音中
        </Box>
      </Box>
    )
  }

  // 録音前は通常の録音開始ボタン
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Tooltip title="録音開始 (Shift+Enter)" placement="top">
        <Box component="span" sx={{ display: 'inline-block' }}>
          <IconButton
            onClick={onClick}
            disabled={disabled}
            sx={{
              width: buttonSize,
              height: buttonSize,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
              '&:disabled': {
                bgcolor: theme.palette.grey[400],
                color: theme.palette.grey[200],
              },
              boxShadow: 2,
              transition: 'all 0.2s ease',
            }}
          >
            <MicIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        </Box>
      </Tooltip>
    </Box>
  )
}

export default SimpleRecordButton
